import { useState, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'wouter';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Upload, 
  Sparkles, 
  Trash2, 
  Edit2, 
  Plus, 
  Image, 
  Loader2, 
  X,
  Camera,
  Palette,
  Crown,
  Download,
  Check,
  Eye,
  Share2
} from 'lucide-react';
import type { CartoonCharacter } from '@shared/schema';
import Navigation from '@/components/Navigation';
import Footer from '@/components/ui/footer';

// Utility function to check if an image URL is likely to be missing
const isImageUrlLikelyMissing = (url: string): boolean => {
  // Check if the URL contains the problematic timestamp pattern
  return url.includes('1758584696785') || url.includes('1758584697250') || url.includes('1758584697589');
};

export default function CharactersPage() {
  const { user, hasActiveSubscription, isFounder } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [characterName, setCharacterName] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [editingCharacter, setEditingCharacter] = useState<CartoonCharacter | null>(null);
  const [editName, setEditName] = useState('');
  const [selectedCharacterForStory, setSelectedCharacterForStory] = useState<CartoonCharacter | null>(null);
  const [previewCharacter, setPreviewCharacter] = useState<CartoonCharacter | null>(null);
  
  const isPremium = hasActiveSubscription || isFounder;

  // Fetch user's cartoon characters
  const { data: characters, isLoading: isLoadingCharacters } = useQuery({
    queryKey: ['/api/cartoon-characters'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/cartoon-characters');
      const data = await response.json();
      return data.characters as CartoonCharacter[];
    },
    enabled: !!user
  });

  // Generate cartoon character mutation
  const generateCharacterMutation = useMutation({
    mutationFn: async ({ name, photoBase64 }: { name: string; photoBase64: string }) => {
      console.log("Generating character:", { name, hasPhoto: !!photoBase64 });
      
      // Use the proper character generation endpoint with OpenAI prompt template
      const response = await apiRequest('POST', '/api/process', {
        characterName: name.trim(),
        base64Image: photoBase64,
        prompt: `Create a cartoon character named ${name.trim()} for a children's storybook.`
      }, {
        timeout: 120000 // Increase timeout to 2 minutes for character generation
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        console.error("Character generation failed:", result);
        throw new Error(result.error || 'Failed to generate character');
      }
      
      console.log("Character generated successfully:", result.character);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/cartoon-characters'] });
      setShowUploadDialog(false);
      setSelectedFile(null);
      setCharacterName('');
      setPreviewUrl(null);
      toast({
        title: "Character created!",
        description: "Your cartoon character has been generated successfully.",
      });
    },
    onError: (error: Error) => {
      console.error("Character generation error:", error);
      
      // Provide more specific error messages
      let errorMessage = "Failed to generate cartoon character. Please try again.";
      
      if (error.message) {
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = "Please log in to generate characters.";
        } else if (error.message.includes('402') || error.message.includes('subscription')) {
          errorMessage = "Premium subscription required for character generation.";
        } else if (error.message.includes('413') || error.message.includes('Request Entity Too Large')) {
          errorMessage = "Image file is too large. Please try with a smaller image (max 50MB).";
        } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
          errorMessage = "Server error occurred. Please try again in a moment.";
        } else if (error.message.includes('timeout')) {
          errorMessage = "Request timed out. Please check your connection and try again.";
        } else if (error.message.includes('OpenAI') || error.message.includes('AI')) {
          errorMessage = "AI generation failed. Please try again.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Generation failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  });

  // Update character mutation
  const updateCharacterMutation = useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const response = await apiRequest('PATCH', `/api/cartoon-characters/${id}`, { name });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/cartoon-characters'] });
      setEditingCharacter(null);
      toast({
        title: "Character updated",
        description: "Character name has been updated successfully.",
      });
    }
  });

  // Delete character mutation
  const deleteCharacterMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('DELETE', `/api/cartoon-characters/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/cartoon-characters'] });
      toast({
        title: "Character deleted",
        description: "Character has been removed from your gallery.",
      });
    }
  });

  // Regenerate character using proper OpenAI Vision + DALL-E (no more placeholders)
  const regenerateCharacterMutation = useMutation({
    mutationFn: async (character: CartoonCharacter) => {
      if (!character.originalPhotoUrl) {
        throw new Error('Cannot regenerate character without original photo');
      }
      
      // Get the original photo and regenerate the character properly
      const photoResponse = await fetch(character.originalPhotoUrl);
      const photoBlob = await photoResponse.blob();
      
      // Convert to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(photoBlob);
      });
      
      const response = await apiRequest('POST', '/api/cartoon-characters/generate', {
        name: character.name,
        photoBase64: base64,
        style: 'storybook',
        ageRange: '3-10'
      });
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/cartoon-characters'] });
      toast({
        title: "Character Regenerated!",
        description: "Your character has been regenerated with improved quality.",
      });
    }
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select an image under 10MB.",
          variant: "destructive",
        });
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerateCharacter = async () => {
    if (!selectedFile || !characterName.trim()) {
      toast({
        title: "Missing information",
        description: "Please provide both a photo and a name for your character.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      await generateCharacterMutation.mutateAsync({
        name: characterName.trim(),
        photoBase64: base64
      });
      setIsGenerating(false);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleUpdateCharacter = () => {
    if (!editingCharacter || !editName.trim()) return;
    
    updateCharacterMutation.mutate({
      id: editingCharacter.id,
      name: editName.trim()
    });
  };

  const handleDeleteCharacter = (character: CartoonCharacter) => {
    if (confirm(`Are you sure you want to delete ${character.name}? This action cannot be undone.`)) {
      deleteCharacterMutation.mutate(character.id);
    }
  };

  const handleUseInStory = (character: CartoonCharacter) => {
    // Store selected character in localStorage for story generation
    localStorage.setItem('selectedCartoonCharacter', JSON.stringify(character));
    setSelectedCharacterForStory(character);
    
    // Show confirmation
    toast({
      title: "Character selected!",
      description: `${character.name} will be used in your next story. Go to Create Story to continue.`,
      action: (
        <Link href="/home">
          <Button size="sm" variant="outline">
            Create Story
          </Button>
        </Link>
      ),
    });
  };

  if (!isPremium) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 p-4 pt-20">
          <div className="max-w-4xl mx-auto">
            <Card className="mt-20">
              <CardContent className="pt-12 pb-8 text-center">
                <Crown className="w-16 h-16 mx-auto mb-4 text-amber-500" />
                <h2 className="text-2xl font-bold mb-4">Premium Feature</h2>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Create custom cartoon characters from photos to use in your personalized story illustrations.
                  Upgrade to premium to unlock this magical feature!
                </p>
                <Link href="/subscribe">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Upgrade to Premium
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 p-4 pt-20">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent mb-2">
                  Character Gallery
                </h1>
                <p className="text-gray-600">
                  Create cartoon characters from photos for your story illustrations
                </p>
              </div>
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="bg-gradient-to-r from-purple-600 to-teal-600 text-white"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Character
              </Button>
            </div>
          </div>

        {/* Selected Character Alert */}
        {selectedCharacterForStory && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>{selectedCharacterForStory.name}</strong> is selected for your next story.{' '}
              <Link href="/home">
                <Button variant="link" className="p-0 h-auto text-green-700 font-semibold">
                  Create Story Now →
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {/* Characters Grid */}
        {isLoadingCharacters ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="w-full h-64 mb-4 rounded-lg" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : characters && characters.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {characters.map((character) => (
              <Card key={character.id} className="overflow-hidden hover:shadow-lg transition-shadow group">
                <div className="aspect-square relative bg-gradient-to-br from-purple-100 to-teal-100">
                  {!isImageUrlLikelyMissing(character.cartoonImageUrl) ? (
                    <>
                      <img
                        src={character.cartoonImageUrl}
                        alt={character.name}
                        className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                        onClick={() => setPreviewCharacter(character)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const placeholder = target.nextElementSibling as HTMLElement;
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                      {/* Preview overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewCharacter(character);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                      </div>
                    </>
                  ) : null}
                  <div 
                    className="w-full h-full flex flex-col items-center justify-center text-gray-500 bg-gradient-to-br from-purple-100 to-teal-100"
                    style={{ display: isImageUrlLikelyMissing(character.cartoonImageUrl) ? 'flex' : 'none' }}
                  >
                    <Image className="w-16 h-16 mb-2 opacity-50" />
                    <span className="text-sm font-medium">{character.name}</span>
                    <span className="text-xs text-gray-400 mb-2">Image not available</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        regenerateCharacterMutation.mutate(character);
                      }}
                      disabled={regenerateCharacterMutation.isPending}
                      className="text-xs"
                    >
                      {regenerateCharacterMutation.isPending ? 'Regenerating...' : 'Regenerate Character'}
                    </Button>
                  </div>
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/90 backdrop-blur-sm"
                      onClick={() => {
                        setEditingCharacter(character);
                        setEditName(character.name);
                      }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/90 backdrop-blur-sm hover:bg-red-50"
                      onClick={() => handleDeleteCharacter(character)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-2">{character.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Created {new Date(character.createdAt).toLocaleDateString()}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setPreviewCharacter(character)}
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                    <Button
                      className="flex-1"
                      variant="outline"
                      onClick={() => handleUseInStory(character)}
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      Use in Story
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = character.cartoonImageUrl;
                        link.download = `${character.name}.png`;
                        link.click();
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12">
            <CardContent className="text-center">
              <Camera className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold mb-2">No characters yet</h3>
              <p className="text-gray-600 mb-6">
                Upload a photo to create your first cartoon character
              </p>
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="bg-gradient-to-r from-purple-600 to-teal-600 text-white"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Your First Character
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Cartoon Character</DialogTitle>
              <DialogDescription>
                Upload a photo to transform into a cartoon character for your stories
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* File Upload */}
              <div>
                <Label htmlFor="photo">Character Photo</Label>
                <div className="mt-2">
                  {previewUrl ? (
                    <div className="relative">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          setSelectedFile(null);
                          setPreviewUrl(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-400 transition-colors"
                    >
                      <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        Click to upload a photo
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG up to 10MB
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="photo"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              </div>

              {/* Character Name */}
              <div>
                <Label htmlFor="name">Character Name</Label>
                <Input
                  id="name"
                  value={characterName}
                  onChange={(e) => setCharacterName(e.target.value)}
                  placeholder="e.g., Princess Sophie"
                  className="mt-2"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowUploadDialog(false);
                  setSelectedFile(null);
                  setCharacterName('');
                  setPreviewUrl(null);
                }}
                disabled={isGenerating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerateCharacter}
                disabled={!selectedFile || !characterName.trim() || isGenerating}
                className="bg-gradient-to-r from-purple-600 to-teal-600 text-white"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Character
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Name Dialog */}
        <Dialog open={!!editingCharacter} onOpenChange={() => setEditingCharacter(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Edit Character Name</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Character Name</Label>
                <Input
                  id="edit-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingCharacter(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateCharacter}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Character Preview Modal */}
        <Dialog open={!!previewCharacter} onOpenChange={() => setPreviewCharacter(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                {previewCharacter?.name}
              </DialogTitle>
              <DialogDescription>
                Created on {previewCharacter ? new Date(previewCharacter.createdAt).toLocaleDateString() : ''}
              </DialogDescription>
            </DialogHeader>
            
            {previewCharacter && (
              <div className="space-y-4">
                <div className="relative">
                  {!isImageUrlLikelyMissing(previewCharacter.cartoonImageUrl) ? (
                    <img 
                      src={previewCharacter.cartoonImageUrl} 
                      alt={previewCharacter.name}
                      className="w-full max-h-96 object-contain rounded-lg border"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const placeholder = target.nextElementSibling as HTMLElement;
                        if (placeholder) {
                          placeholder.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div 
                    className="w-full h-96 flex flex-col items-center justify-center text-gray-500 bg-gradient-to-br from-purple-100 to-teal-100 rounded-lg border"
                    style={{ display: isImageUrlLikelyMissing(previewCharacter.cartoonImageUrl) ? 'flex' : 'none' }}
                  >
                    <Image className="w-20 h-20 mb-4 opacity-50" />
                    <span className="text-lg font-medium">{previewCharacter.name}</span>
                    <span className="text-sm text-gray-400 mb-4">Image not available</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => regenerateCharacterMutation.mutate(previewCharacter)}
                      disabled={regenerateCharacterMutation.isPending}
                    >
                      {regenerateCharacterMutation.isPending ? 'Regenerating...' : 'Regenerate Character'}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Character Name</p>
                    <p className="text-lg font-semibold">{previewCharacter.name || 'Unnamed Character'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Created</p>
                    <p className="text-sm">{previewCharacter.createdAt ? new Date(previewCharacter.createdAt).toLocaleString() : 'Unknown'}</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    onClick={() => {
                      if (previewCharacter.cartoonImageUrl) {
                        navigator.clipboard.writeText(previewCharacter.cartoonImageUrl);
                        toast({
                          title: "Copied!",
                          description: "Character image URL copied to clipboard.",
                        });
                      }
                    }}
                    variant="outline"
                    size="sm"
                  >
                    <Share2 className="h-4 w-4 mr-1" />
                    Copy URL
                  </Button>
                  <Button 
                    onClick={() => {
                      if (previewCharacter.cartoonImageUrl) {
                        const link = document.createElement('a');
                        link.href = previewCharacter.cartoonImageUrl;
                        link.download = `${previewCharacter.name}-character.png`;
                        link.click();
                      }
                    }}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                  <Button 
                    onClick={() => handleUseInStory(previewCharacter)}
                    className="flex-1"
                  >
                    <Palette className="h-4 w-4 mr-1" />
                    Use in Story
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <Footer />
    </>
  );
}